//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * Validates a URL for safe redirection.
 * @param url - The URL to validate for safe redirection.
 * @returns True if the URL is safe for redirection, false otherwise.
 */
export const isSafeRedirectUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;

  // Explicitly reject protocol-relative URLs (e.g. //example.com)
  if (url.startsWith('//')) return false;

  try {
    const currentOrigin = window.location.origin;

    // If it's an absolute URL, require same origin
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const target = new URL(url);
      return target.origin === currentOrigin;
    }

    // Allow only root-relative paths (but not //... which we already rejected)
    return url.startsWith('/');
  } catch {
    return false;
  }
};

/**
 * Validates an OAuth/external authentication URL for safe navigation.
 * Unlike isSafeRedirectUrl, this allows cross-origin HTTPS URLs since
 * OAuth flows require navigating to external identity providers.
 * Rejects dangerous schemes (javascript:, data:, vbscript:) and non-HTTPS URLs.
 * @param url - The URL to validate.
 * @returns True if the URL is a valid HTTPS URL safe for OAuth navigation.
 */
/**
 * Map an iframe-synced product path back to its React Router solution route.
 *
 * SwitcherIframe uses history.replaceState to sync the browser URL with the
 * iframe's internal URL (e.g. /plan/#/ccmweb/welcome). That URL uses the
 * product's ingress path (/plan), not the React Router route (/solution/plan).
 * After session expiry + re-auth, the saved return URL would hit the 404
 * catch-all. This function resolves it back to the correct solution route.
 *
 * Each solution in the config has:
 *   - path: the product's actual URL (e.g. https://host/plan/)
 *   - routePath: the React Router route (e.g. /solution/plan)
 *
 * We extract the first path segment from `path` and compare it with the first
 * segment of `returnPath`. If they match, we swap it for `routePath` and
 * preserve the hash fragment.
 */
export function mapProductPathToSolutionRoute(
  returnPath: string,
  solutions: unknown,
): string {
  if (!Array.isArray(solutions) || !returnPath || returnPath === '/') {
    return returnPath;
  }

  // Parse returnPath to extract pathname and hash separately
  let pathname: string;
  let hash = '';
  const hashIndex = returnPath.indexOf('#');
  if (hashIndex !== -1) {
    pathname = returnPath.slice(0, hashIndex);
    hash = returnPath.slice(hashIndex);
  } else {
    pathname = returnPath;
  }

  // Extract the first path segment (e.g. "/plan" from "/plan/" or "/plan/foo")
  const firstSegment = '/' + (pathname.split('/').find(Boolean) || '');

  for (const solution of solutions) {
    if (!solution?.path || !solution?.routePath) continue;
    try {
      const productUrl = new URL(solution.path, 'https://placeholder');
      const productSegment =
        '/' + (productUrl.pathname.split('/').find(Boolean) || '');
      if (
        productSegment !== '/' &&
        firstSegment.toLowerCase() === productSegment.toLowerCase()
      ) {
        // Found a matching product — return the solution routePath with hash
        const routePath = solution.routePath.endsWith('/')
          ? solution.routePath.slice(0, -1)
          : solution.routePath;
        return `${routePath}${hash}`;
      }
    } catch {
      continue;
    }
  }

  return returnPath;
}

export const isSafeOAuthUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;

  // Reject protocol-relative URLs
  if (url.startsWith('//')) return false;

  try {
    const target = new URL(url);
    // Only allow HTTPS for OAuth (never HTTP, javascript:, data:, etc.)
    return target.protocol === 'https:';
  } catch {
    return false;
  }
};
